<template>
  <v-navigation-drawer
    v-model="isDrawerOpen"
    :temporary="$vuetify.breakpoint.mdAndDown"
    app
    width="280"
    class="drawer-root"
    floating
  >
    <div class="pa-8 d-flex align-center">
      <div class="brand-text d-flex flex-column">
        <div class="brand-wrapper">
          <span class="brand-main">TSA</span>
          <span class="brand-sub-accent">AUTO</span>
        </div>
        <span class="brand-tagline">Management System</span>
      </div>
    </div>

    <div class="drawer-scroll-area px-4">
      <v-list nav flat class="pa-0">
        
        <template v-if="filteredMenuItems.length > 0">
          <p class="section-header ml-4 mt-2">Core Services</p>

          <v-list-item
            v-for="item in filteredMenuItems"
            :key="item.title"
            :to="item.link"
            exact
            active-class="active-nav-item"
            class="nav-item mb-2"
          >
            <v-list-item-icon class="mr-4">
              <v-icon size="20">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="nav-title">{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="filteredGroups.length > 0">
          <p class="section-header ml-4 mt-6">Administration</p>

          <v-list-group
            v-for="group in filteredGroups"
            :key="group.title"
            :prepend-icon="group.icon"
            active-class="active-group-header"
            class="nav-group mb-2"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="nav-title">{{ group.title }}</v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="child in group.children"
              :key="child.title"
              :to="child.link"
              exact
              active-class="active-sub-item"
              class="sub-nav-item"
            >
              <v-list-item-content>
                <v-list-item-title>{{ child.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>

      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { DataService } from "@/services/DataService";

export default {
  name: "NavigationDrawer",
  props: { value: Boolean },
  data() {
    return {
      menuItems: [
        { id: 1, title: "แดชบอร์ด", icon: "mdi-view-dashboard-outline", link: "/" },
        { id: 2, title: "จัดการคำสั่งซื้อ", icon: "mdi-cart-outline", link: "/orders" },
        { id: 3, title: "จัดการสินค้า", icon: "mdi-package-variant-closed", link: "/product" },
        { id: 4, title: "จัดการลูกค้า", icon: "mdi-account-group-outline", link: "/customer" },
        { id: 5, title: "จัดการโปรโมชั่น", icon: "mdi-tag-heart-outline", link: "/promotion" },
        { id: 6, title: "จัดการวิดีโอโปรโมท", icon: "mdi-tag-heart-outline", link: "/videopromote" },
        { id: 7, title: "จัดการแคตตาล็อก", icon: "mdi-tag-heart-outline", link: "/catalog" },
        {
          title: "การตั้งค่า",
          icon: "mdi-cog-outline",
          children: [
            { id: 1, title: "ผู้ใช้งาน", link: "/user" },
            { id: 2, title: "กลุ่มลูกค้า", link: "/groupcustomers" },
            { id: 3, title: "หมวดหมู่หลัก", link: "/categories" },
            { id: 4, title: "หมวดหมู่ย่อย", link: "/subcategories" },
            { id: 5, title: "กลุ่มสินค้า", link: "/itemGroups" },
            { id: 6, title: "ยันยัน Email", link: "/verifyemail" },
            { id: 7, title: "รีเซตรหัสผ่าน", link: "/changepassword" },
          ],
        },
      ],
    };
  },

  computed: {
    ...mapState("authen", ["user"]),
    ...mapState("permit", { permitData: "items" }),

    isDrawerOpen: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },

    currentPermit() {
      if (!this.permitData || !this.user) return null;
      return Array.isArray(this.permitData)
        ? this.permitData.find((obj) => obj.memberId === this.user.id)
        : this.permitData;
    },

    parsedSettingRead() {
      const val = this.currentPermit?.settingRead;
      if (!val) return [];
      try { return typeof val === "string" ? JSON.parse(val) : val; } catch (e) { return []; }
    },

    parsedManagementRead() {
      const val = this.currentPermit?.managementRead;
      if (!val) return [];
      try { return typeof val === "string" ? JSON.parse(val) : val; } catch (e) { return []; }
    },

    /* 🔥 แก้ไขจุดนี้: กรองเมนูหลักชั้นนอกสุด อันไหนไม่มีสิทธิ์ให้คัดออกทันที */
    filteredMenuItems() {
      return this.menuItems
        .filter((item) => !item.children)
        .filter((item) => this.checkAccessLogic(item, ""));
    },

    /* 🔥 แก้ไขจุดนี้: กรองเมนูแบบกลุ่ม (Dropdown) รวมถึงลูกๆ ข้างใน 
       ถ้าลูกคนไหนไม่มีสิทธิ์จะหายไป และถ้าในกลุ่มนั้นไม่เหลือลูกที่มีสิทธิ์เลย ตัวกลุ่มใหญ่ก็จะหายไปด้วย */
    filteredGroups() {
      return this.menuItems
        .filter((item) => item.children)
        .map((group) => {
          // กรองเอาเฉพาะลูกที่มีสิทธิ์เข้าถึงจริง ๆ
          const allowedChildren = group.children.filter((child) =>
            this.checkAccessLogic(child, group.title)
          );
          // คืนค่ารูปแบบกลุ่มใหม่ที่มีเฉพาะลูกที่ได้รับอนุญาต
          return { ...group, children: allowedChildren };
        })
        // คัดกรองกรุ๊ปทิ้งไปเลย ถ้ากลุ่มนั้นๆ ไม่มีลูกหลงเหลืออยู่เลยแม้แต่คนเดียว
        .filter((group) => group.children.length > 0);
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    ...mapActions("permit", ["upsertItem"]),

    async loadData() {
      try {
        const uid = this.user?.id;
        if (!uid) return;
        const res = await DataService.permit.findByPk(uid);
        if (res && res.data) {
          await this.upsertItem(res.data);
        }
      } catch (error) {
        console.error("Failed to load permit:", error);
      }
    },

    /* 💡 ย้ายลอจิกการตรวจสอบสิทธิ์มาไว้ที่ฟังก์ชันภายในเพื่อให้เรียกใช้ผ่าน Computed ได้สมบูรณ์ขึ้น */
    checkAccessLogic(item, groupTitle = "") {
      const id = Number(item.id);
      if (groupTitle === "การตั้งค่า") {
        return this.parsedSettingRead.includes(id);
      }
      return this.parsedManagementRead.includes(id);
    },
  },
};
</script>

<style scoped>
/* โค้ดส่วน CSS คงไว้เหมือนเดิมทุกประการตามสไตล์เดิมของคุณครับ */
.brand-wrapper { display: flex; align-items: baseline; }
.brand-main { font-size: 1.6rem; font-weight: 900; color: #1e293b; letter-spacing: -1px; text-transform: uppercase; }
.brand-sub-accent { font-size: 1.5rem; font-weight: 300; color: #283593; margin-left: 4px; letter-spacing: 1px; text-transform: uppercase; background: linear-gradient(135deg, #4f46e5, #9333ea); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.brand-tagline { font-size: 0.65rem; font-weight: 500; color: #94a3b8; text-transform: uppercase; letter-spacing: 2px; margin-top: -2px; }
.drawer-root { background-color: #ffffff !important; border-right: 1px solid #f1f5f9 !important; }
.section-header { font-size: 0.65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 8px; }
.nav-item { border-radius: 12px !important; min-height: 48px; color: #64748b !important; }
.active-nav-item { background-color: #eef2ff !important; color: #283593 !important; }
.active-nav-item .v-icon { color: #283593 !important; }
.nav-title { font-size: 0.9rem; font-weight: 600; }
::v-deep .v-list-group__header.active-group-header { color: #283593 !important; }
.sub-nav-item { margin-left: 12px !important; border-radius: 10px !important; min-height: 40px !important; color: #64748b !important; }
.active-sub-item { background-color: #f8fafc !important; color: #283593 !important; font-weight: 700; }
.drawer-scroll-area { height: calc(100vh - 120px); overflow-y: auto; }
.drawer-scroll-area::-webkit-scrollbar { width: 4px; }
.drawer-scroll-area::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
</style>